import { el, append } from "./dom";
import { effect, type Signal } from "./signals";

type Child = Node | string | number | null | undefined | false;

/** A titled panel. */
export function card(opts: { title?: string; subtitle?: string; children: Child[] }): HTMLElement {
  return el(
    "section",
    { class: "card" },
    opts.title ? el("h3", { class: "card-title" }, opts.title) : null,
    opts.subtitle ? el("p", { class: "card-subtitle" }, opts.subtitle) : null,
    ...opts.children,
  );
}

/** A labeled range slider bound to a numeric signal. */
export function rangeControl(opts: {
  label: string;
  min: number;
  max: number;
  step?: number;
  signal: Signal<number>;
  format?: (n: number) => string;
  hint?: string;
}): HTMLElement {
  const value = el("span", { class: "control-value" });
  const input = el("input", {
    type: "range",
    min: String(opts.min),
    max: String(opts.max),
    step: String(opts.step ?? 1),
    value: String(opts.signal.value),
    class: "range",
    oninput: (e: Event) => {
      opts.signal.value = Number((e.target as HTMLInputElement).value);
    },
  }) as HTMLInputElement;

  effect(() => {
    const v = opts.signal.value;
    value.textContent = (opts.format ?? String)(v);
    if (Number(input.value) !== v) input.value = String(v);
  });

  return el(
    "label",
    { class: "control" },
    el("span", { class: "control-label" }, opts.label, value),
    input,
    opts.hint ? el("span", { class: "control-hint" }, opts.hint) : null,
  );
}

/** A labeled dropdown bound to a string signal. */
export function selectControl(opts: {
  label: string;
  options: Array<{ value: string; label: string }>;
  signal: Signal<string>;
}): HTMLElement {
  const select = el("select", {
    class: "select",
    onchange: (e: Event) => {
      opts.signal.value = (e.target as HTMLSelectElement).value;
    },
  }) as HTMLSelectElement;
  for (const o of opts.options) {
    const option = el("option", { value: o.value }, o.label) as HTMLOptionElement;
    if (o.value === opts.signal.value) option.selected = true;
    select.appendChild(option);
  }
  effect(() => {
    if (select.value !== opts.signal.value) select.value = opts.signal.value;
  });
  return el(
    "label",
    { class: "control" },
    el("span", { class: "control-label" }, opts.label),
    select,
  );
}

/** A checkbox bound to a boolean signal. */
export function toggleControl(opts: { label: string; signal: Signal<boolean>; hint?: string }): HTMLElement {
  const input = el("input", {
    type: "checkbox",
    class: "checkbox",
    onchange: (e: Event) => {
      opts.signal.value = (e.target as HTMLInputElement).checked;
    },
  }) as HTMLInputElement;
  effect(() => {
    input.checked = opts.signal.value;
  });
  return el(
    "label",
    { class: "control control-toggle" },
    input,
    el("span", { class: "control-label" }, opts.label),
    opts.hint ? el("span", { class: "control-hint" }, opts.hint) : null,
  );
}

export function button(opts: {
  label: string;
  onClick: () => void;
  variant?: "primary" | "ghost";
}): HTMLElement {
  return el(
    "button",
    {
      class: `btn ${opts.variant === "primary" ? "btn-primary" : "btn-ghost"}`,
      type: "button",
      onclick: opts.onClick,
    },
    opts.label,
  );
}

export function buttonRow(...buttons: Child[]): HTMLElement {
  return el("div", { class: "button-row" }, ...buttons);
}

/** A live-updating statistic. `value` is re-read whenever its signals change. */
export function stat(opts: { label: string; value: () => string; hint?: string; accent?: boolean }): HTMLElement {
  const val = el("div", { class: `stat-value ${opts.accent ? "accent" : ""}` });
  effect(() => {
    val.textContent = opts.value();
  });
  return el(
    "div",
    { class: "stat" },
    el("div", { class: "stat-label" }, opts.label),
    val,
    opts.hint ? el("div", { class: "stat-hint" }, opts.hint) : null,
  );
}

export function statRow(...stats: Child[]): HTMLElement {
  return el("div", { class: "stat-row" }, ...stats);
}

export function callout(kind: "info" | "tip" | "warn", ...children: Child[]): HTMLElement {
  return el("div", { class: `callout callout-${kind}` }, ...children);
}

export function codeBlock(code: string): HTMLElement {
  const btn = el(
    "button",
    {
      class: "code-copy",
      type: "button",
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(code);
          btn.textContent = "Copied ✓";
          setTimeout(() => (btn.textContent = "Copy"), 1300);
        } catch {
          /* clipboard unavailable — ignore */
        }
      },
    },
    "Copy",
  );
  return el("div", { class: "code-wrap" }, btn, el("pre", { class: "code" }, el("code", {}, code)));
}

/** A tab strip bound to a string signal; each tab lazily renders its content. */
export function tabs(opts: {
  signal: Signal<string>;
  tabs: Array<{ id: string; label: string; render: () => Node }>;
}): HTMLElement {
  const strip = el("div", { class: "tabstrip", role: "tablist" });
  const panel = el("div", { class: "tabpanel" });

  const buttons = opts.tabs.map((t) =>
    el(
      "button",
      {
        class: "tab",
        type: "button",
        role: "tab",
        onclick: () => {
          opts.signal.value = t.id;
        },
      },
      t.label,
    ),
  );
  buttons.forEach((b) => strip.appendChild(b));

  effect(() => {
    const active = opts.signal.value;
    opts.tabs.forEach((t, i) => {
      buttons[i].classList.toggle("active", t.id === active);
    });
    while (panel.firstChild) panel.removeChild(panel.firstChild);
    const current = opts.tabs.find((t) => t.id === active) ?? opts.tabs[0];
    append(panel, current.render());
  });

  return el("div", { class: "tabs" }, strip, panel);
}

/** A page header block. */
export function pageHeader(opts: { eyebrow?: string; title: string; lede?: string }): HTMLElement {
  return el(
    "header",
    { class: "page-header" },
    opts.eyebrow ? el("div", { class: "eyebrow" }, opts.eyebrow) : null,
    el("h1", { class: "page-title" }, opts.title),
    opts.lede ? el("p", { class: "lede" }, opts.lede) : null,
  );
}

/** Prose section with a heading. */
export function prose(...children: Child[]): HTMLElement {
  return el("div", { class: "prose" }, ...children);
}
